import type { HourBalance } from "@/api/types/membership";

/**
 * Hour-bank / studio-window display helpers — Membership + Hour-Bank
 * Engine (Phase 2b). Pure, display-only functions: every actual quota/
 * capacity decision is server-side (app/services/hour_bank.py /
 * app/services/studio_capacity.py) — nothing here computes a number the
 * API is the source of truth for, same "preview/display only" posture as
 * features/bookings/bookingMath.ts and features/quotes/quoteMath.ts.
 */

/** "4h" / "1h" — whole-hour studio time, never fractional in this system
 * (2-hour-increment booking discipline). */
export function formatHours(hours: number): string {
  return `${hours}h`;
}

/** "4h/wk" for a capped tier, "Unlimited" for `weekly_hour_quota: null`
 * (Crow, as currently seeded). */
export function formatQuota(quota: number | null): string {
  return quota === null ? "Unlimited" : `${quota}h/wk`;
}

/** "2h / 4h this week" or "2h used (unlimited)" for the balance card. */
export function formatBalanceSummary(balance: Pick<HourBalance, "used" | "quota">): string {
  if (balance.quota === null) return `${balance.used}h used this week (unlimited)`;
  return `${balance.used}h / ${balance.quota}h this week`;
}

/** 0-100 usage percentage for a progress bar; `null` when the tier has no
 * quota to measure against (unlimited) or the quota is 0. */
export function usagePercent(balance: Pick<HourBalance, "used" | "quota">): number | null {
  if (balance.quota === null || balance.quota <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((balance.used / balance.quota) * 100)));
}

/** True once `remaining` hits zero (or below, which should never happen
 * given server-side overdraft rejection, but the UI treats it the same
 * as exactly-zero either way). */
export function isQuotaExhausted(balance: Pick<HourBalance, "remaining">): boolean {
  return balance.remaining !== null && balance.remaining <= 0;
}

/** "Mar 3 – Mar 9" from two "YYYY-MM-DD" ISO date strings — pure string
 * parsing (no `Date` construction from the ISO string directly) to avoid
 * the classic UTC-midnight-shifts-a-day-back timezone pitfall Date
 * parsing of bare "YYYY-MM-DD" strings has in non-UTC browsers. */
export function formatWeekRange(weekStartIso: string, weekEndIso: string): string {
  return `${formatShortDate(weekStartIso)} – ${formatShortDate(weekEndIso)}`;
}

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatShortDate(iso: string): string {
  const [, month, day] = iso.split("-").map(Number);
  return `${MONTH_ABBR[month - 1]} ${day}`;
}

/** "9:00 AM" from a "HH:MM:SS" (or "HH:MM") time string — pure string
 * parsing, same timezone-pitfall avoidance as formatWeekRange above (a
 * bare time string has no date/zone context to construct a `Date` from
 * safely). */
export function formatTime(time: string): string {
  const [hourStr, minuteStr] = time.split(":");
  const hour24 = Number(hourStr);
  const minute = Number(minuteStr);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
}

/** "9:00 AM – 11:00 AM" */
export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} – ${formatTime(endTime)}`;
}

/** Hours implied by a contiguous span of 2-hour studio windows — mirrors
 * app/services/studio_capacity.py::hours_for_window_span exactly
 * (windowCount * 2). Used by the book-session dialog's live preview
 * before the server's own response confirms `hours`. */
export function hoursForWindowSpan(windowCount: number): number {
  return windowCount * 2;
}
